'use client'

import { useState } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/ui-dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Button } from '@/components/ui/button'
import { FiCopy, FiCheck, FiCode, FiBook, FiZap } from 'react-icons/fi'
import { SiJavascript, SiPython, SiPhp } from 'react-icons/si'
import toast from 'react-hot-toast'

interface ApiDocumentationModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export default function ApiDocumentationModal({ open, onOpenChange }: ApiDocumentationModalProps) {
  const [copiedCode, setCopiedCode] = useState<string | null>(null)

  const copyCode = (code: string, id: string) => {
    navigator.clipboard.writeText(code)
    setCopiedCode(id)
    toast.success('Código copiado')
    setTimeout(() => setCopiedCode(null), 2000)
  }

  const CodeBlock = ({ code, language, id }: { code: string; language: string; id: string }) => (
    <div className="relative group">
      <div className="absolute top-2 right-2 z-10">
        <Button
          size="sm"
          variant="ghost"
          onClick={() => copyCode(code, id)}
          className="opacity-0 group-hover:opacity-100 transition-opacity bg-slate-700 hover:bg-slate-600 text-white"
        >
          {copiedCode === id ? <FiCheck className="h-4 w-4" /> : <FiCopy className="h-4 w-4" />}
        </Button>
      </div>
      <pre className="bg-slate-900 text-slate-100 p-4 rounded-lg overflow-x-auto text-[11px] sm:text-sm border border-slate-700 font-mono">
        <code className={`language-${language}`}>{code}</code>
      </pre>
    </div>
  )

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-2xl">
            <FiBook className="h-6 w-6 text-blue-600" />
            Documentación de API - Validación de Identidad
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 mt-4">
          {/* Introducción */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="font-bold text-blue-900 flex items-center gap-2 mb-2">
              <FiZap className="h-5 w-5" />
              Inicio Rápido
            </h3>
            <p className="text-sm text-blue-800 leading-relaxed">
              Todas nuestras APIs utilizan el estándar <strong>REST</strong> y devuelven respuestas en formato <strong>JSON</strong>. 
              Necesitas incluir tu token de autorización en cada petición.
            </p>
          </div>

          {/* Autenticación */}
          <div>
            <h3 className="font-bold text-lg mb-3 flex items-center gap-2">
              <FiCode className="h-5 w-5 text-purple-600" />
              Autenticación
            </h3>
            <p className="text-sm text-gray-600 mb-3">
              Incluye tu token en el header <code className="bg-slate-100 px-2 py-1 rounded text-purple-600">Authorization</code>:
            </p>
            <CodeBlock
              id="auth-header"
              language="http"
              code={`Authorization: Bearer TU_TOKEN_AQUI`}
            />
          </div>

          {/* Endpoints */}
          <div>
            <h3 className="font-bold text-lg mb-3">Endpoints Disponibles</h3>
            
            <Tabs defaultValue="dni" className="w-full">
              <TabsList className="grid w-full grid-cols-1 sm:grid-cols-3 h-auto mb-4 gap-1 p-1 bg-gray-100/50 rounded-xl">
                <TabsTrigger value="dni" className="rounded-lg py-2 data-[state=active]:bg-white data-[state=active]:shadow-sm">Consulta DNI</TabsTrigger>
                <TabsTrigger value="ruc" className="rounded-lg py-2 data-[state=active]:bg-white data-[state=active]:shadow-sm">Consulta RUC</TabsTrigger>
                <TabsTrigger value="cpe" className="rounded-lg py-2 data-[state=active]:bg-white data-[state=active]:shadow-sm">Validar CPE</TabsTrigger>
              </TabsList>

              {/* DNI */}
              <TabsContent value="dni" className="space-y-4">
                <div className="bg-slate-50 p-3 rounded-lg border">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="bg-green-500 text-white text-xs font-bold px-2 py-1 rounded">GET</span>
                    <code className="text-sm font-mono">https://miapi.cloud/v1/dni/{'{numero}'}</code>
                  </div>
                  <p className="text-xs text-gray-600">Obtiene información de una persona por su número de DNI.</p>
                </div>

                <div className="space-y-3">
                  <h4 className="font-semibold text-sm flex items-center gap-2">
                    <SiJavascript className="text-yellow-500" /> JavaScript / Node.js
                  </h4>
                  <CodeBlock
                    id="dni-js"
                    language="javascript"
                    code={`// Usando fetch
const consultarDNI = async (dni) => {
  const response = await fetch(\`https://miapi.cloud/v1/dni/\${dni}\`, {
    headers: {
      'Authorization': 'Bearer TU_TOKEN_AQUI'
    }
  });
  const data = await response.json();
  console.log(data);
};

consultarDNI('12345678');`}
                  />

                  <h4 className="font-semibold text-sm flex items-center gap-2 mt-4">
                    <SiPython className="text-blue-500" /> Python
                  </h4>
                  <CodeBlock
                    id="dni-py"
                    language="python"
                    code={`import requests

def consultar_dni(dni):
    headers = {
        'Authorization': 'Bearer TU_TOKEN_AQUI'
    }
    response = requests.get(
        f'https://miapi.cloud/v1/dni/{dni}',
        headers=headers
    )
    return response.json()

resultado = consultar_dni('12345678')
print(resultado)`}
                  />

                  <h4 className="font-semibold text-sm flex items-center gap-2 mt-4">
                    <SiPhp className="text-purple-500" /> PHP
                  </h4>
                  <CodeBlock
                    id="dni-php"
                    language="php"
                    code={`<?php
$dni = '12345678';
$token = 'TU_TOKEN_AQUI';

$ch = curl_init();
curl_setopt($ch, CURLOPT_URL, "https://miapi.cloud/v1/dni/$dni");
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
curl_setopt($ch, CURLOPT_HTTPHEADER, [
    "Authorization: Bearer $token"
]);

$response = curl_exec($ch);
$data = json_decode($response, true);
curl_close($ch);

print_r($data);
?>`}
                  />

                  <h4 className="font-semibold text-sm mt-4">Respuesta Exitosa</h4>
                  <CodeBlock
                    id="dni-response"
                    language="json"
                    code={`{
  "success": true,
  "datos": {
    "numero": "12345678",
    "nombre_completo": "JUAN PEREZ GARCIA",
    "nombres": "JUAN",
    "apellido_paterno": "PEREZ",
    "apellido_materno": "GARCIA",
    "codigo_verificacion": "1"
  }
}`}
                  />
                </div>
              </TabsContent>

              {/* RUC */}
              <TabsContent value="ruc" className="space-y-4">
                <div className="bg-slate-50 p-3 rounded-lg border">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="bg-green-500 text-white text-xs font-bold px-2 py-1 rounded">GET</span>
                    <code className="text-sm font-mono">https://miapi.cloud/v1/ruc/{'{numero}'}</code>
                  </div>
                  <p className="text-xs text-gray-600">Obtiene información de una empresa por su número de RUC.</p>
                </div>

                <div className="space-y-3">
                  <h4 className="font-semibold text-sm flex items-center gap-2">
                    <SiJavascript className="text-yellow-500" /> JavaScript / Node.js
                  </h4>
                  <CodeBlock
                    id="ruc-js"
                    language="javascript"
                    code={`const consultarRUC = async (ruc) => {
  const response = await fetch(\`https://miapi.cloud/v1/ruc/\${ruc}\`, {
    headers: {
      'Authorization': 'Bearer TU_TOKEN_AQUI'
    }
  });
  const data = await response.json();
  console.log(data);
};

consultarRUC('20123456789');`}
                  />

                  <h4 className="font-semibold text-sm flex items-center gap-2 mt-4">
                    <SiPython className="text-blue-500" /> Python
                  </h4>
                  <CodeBlock
                    id="ruc-py"
                    language="python"
                    code={`import requests

def consultar_ruc(ruc):
    headers = {
        'Authorization': 'Bearer TU_TOKEN_AQUI'
    }
    response = requests.get(
        f'https://miapi.cloud/v1/ruc/{ruc}',
        headers=headers
    )
    return response.json()

resultado = consultar_ruc('20123456789')
print(resultado)`}
                  />

                  <h4 className="font-semibold text-sm mt-4">Respuesta Exitosa</h4>
                  <CodeBlock
                    id="ruc-response"
                    language="json"
                    code={`{
  "success": true,
  "datos": {
    "ruc": "20123456789",
    "razon_social": "EMPRESA EJEMPLO S.A.C.",
    "nombre_comercial": "EJEMPLO",
    "estado": "ACTIVO",
    "condicion": "HABIDO",
    "domiciliado": {
      "direccion": "AV. EJEMPLO 123",
      "departamento": "LIMA",
      "provincia": "LIMA",
      "distrito": "MIRAFLORES",
      "ubigeo": "150122"
    }
  }
}`}
                  />
                </div>
              </TabsContent>

              {/* CPE */}
              <TabsContent value="cpe" className="space-y-4">
                <div className="bg-slate-50 p-3 rounded-lg border">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="bg-blue-500 text-white text-xs font-bold px-2 py-1 rounded">POST</span>
                    <code className="text-sm font-mono">https://miapi.cloud/v1/validarcpe</code>
                  </div>
                  <p className="text-xs text-gray-600">Valida un comprobante electrónico (CPE) en SUNAT.</p>
                </div>

                <div className="bg-slate-50 p-4 rounded-lg border space-y-2">
                  <h4 className="font-semibold text-xs text-slate-700 uppercase tracking-wider">Parámetros del JSON (Cuerpo de la Solicitud)</h4>
                  <ul className="text-xs text-slate-600 space-y-1 font-mono">
                    <li><strong className="text-blue-600">numRuc / ruc:</strong> RUC de la empresa emisora (11 dígitos).</li>
                    <li><strong className="text-blue-600">codComp / tipo_documento:</strong> Código del comprobante (01 = Factura, 03 = Boleta, 07 = Nota de Crédito, 08 = Nota de Débito, R1 = Recibo por Honorarios).</li>
                    <li><strong className="text-blue-600">numeroSerie / serie:</strong> Serie del comprobante (ejemplo: F005).</li>
                    <li><strong className="text-blue-600">numero:</strong> Número correlativo del comprobante (ejemplo: 1567).</li>
                    <li><strong className="text-blue-600">fechaEmision / fecha_emision:</strong> Fecha de emisión en formato DD/MM/YYYY (ejemplo: 24/09/2026).</li>
                    <li><strong className="text-blue-600">monto:</strong> Importe total (ejemplo: 504.00).</li>
                    <li><strong className="text-blue-600">codDocRecep / cod_doc_recep:</strong> Tipo de documento del cliente/receptor (6 = RUC, 1 = DNI, 4 = Carnet Extranjería, 7 = Pasaporte).</li>
                    <li><strong className="text-blue-600">numDocRecep / num_doc_recep:</strong> Número de documento del cliente/receptor (Requerido para Facturas 01).</li>
                  </ul>
                </div>

                <div className="space-y-3">
                  <h4 className="font-semibold text-sm flex items-center gap-2">
                    <SiJavascript className="text-yellow-500" /> JavaScript / Node.js
                  </h4>
                  <CodeBlock
                    id="cpe-js"
                    language="javascript"
                    code={`const validarCPE = async (datos) => {
  const response = await fetch('https://miapi.cloud/v1/validarcpe', {
    method: 'POST',
    headers: {
      'Authorization': 'Bearer TU_TOKEN_AQUI',
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(datos)
  });
  const data = await response.json();
  console.log(data);
};

validarCPE({
  numRuc: "20608530291",
  codComp: "01",
  numeroSerie: "F005",
  numero: "1567",
  fechaEmision: "24/09/2026",
  monto: "504.00",
  codDocRecep: "6",
  numDocRecep: "20615138356"
});`}
                  />

                  <h4 className="font-semibold text-sm flex items-center gap-2 mt-4">
                    <SiPython className="text-blue-500" /> Python
                  </h4>
                  <CodeBlock
                    id="cpe-py"
                    language="python"
                    code={`import requests

def validar_cpe(datos):
    headers = {
        'Authorization': 'Bearer TU_TOKEN_AQUI',
        'Content-Type': 'application/json'
    }
    response = requests.post(
        'https://miapi.cloud/v1/validarcpe',
        headers=headers,
        json=datos
    )
    return response.json()

datos_cpe = {
    "numRuc": "20608530291",
    "codComp": "01",
    "numeroSerie": "F005",
    "numero": "1567",
    "fechaEmision": "24/09/2026",
    "monto": "504.00",
    "codDocRecep": "6",
    "numDocRecep": "20615138356"
}

resultado = validar_cpe(datos_cpe)
print(resultado)`}
                  />

                  <h4 className="font-semibold text-sm flex items-center gap-2 mt-4">
                    <SiPhp className="text-purple-500" /> PHP
                  </h4>
                  <CodeBlock
                    id="cpe-php"
                    language="php"
                    code={`<?php
$token = 'TU_TOKEN_AQUI';
$payload = [
    'numRuc' => '20608530291',
    'codComp' => '01',
    'numeroSerie' => 'F005',
    'numero' => '1567',
    'fechaEmision' => '24/09/2026',
    'monto' => '504.00',
    'codDocRecep' => '6',
    'numDocRecep' => '20615138356'
];

$ch = curl_init('https://miapi.cloud/v1/validarcpe');
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
curl_setopt($ch, CURLOPT_POST, true);
curl_setopt($ch, CURLOPT_HTTPHEADER, [
    "Authorization: Bearer $token",
    "Content-Type: application/json"
]);
curl_setopt($ch, CURLOPT_POSTFIELDS, json_encode($payload));

$response = curl_exec($ch);
$data = json_decode($response, true);
curl_close($ch);

print_r($data);
?>`}
                  />

                  <h4 className="font-semibold text-sm mt-4">Respuesta Exitosa</h4>
                  <CodeBlock
                    id="cpe-response"
                    language="json"
                    code={`{
  "data": {
    "numero": "1567",
    "numeroSerie": "F005",
    "codComp": "01",
    "fechaEmision": "24/09/2026",
    "numRuc": "20608530291",
    "codDocRecep": "6",
    "numDocRecep": "20615138356",
    "monto": "504.00",
    "estadoCp": "1",
    "estadoRuc": "00",
    "condDomiRuc": "00",
    "observaciones": [],
    "estadoCpDesc": "ACEPTADO",
    "estadoCpDetalle": "ACEPTADO (Válido)",
    "estadoRucDesc": "ACTIVO",
    "condDomiRucDesc": "HABIDO",
    "codCompDesc": "Factura",
    "codDocRecepDesc": "RUC",
    "estadoComprobanteTexto": "Estado del comprobante a la fecha de la consulta : ACEPTADO",
    "estadoContribuyenteTexto": "Estado del contribuyente a la fecha de emisión : ACTIVO",
    "condicionDomicilioTexto": "Condición de domicilio a la fecha de emisión : HABIDO",
    "desglose": {
      "estadoComprobante": "estadoCp: '1' (ACEPTADO)",
      "estadoContribuyente": "estadoRuc: '00' (ACTIVO)",
      "condicionDomicilio": "condDomiRuc: '00' (HABIDO)",
      "tipoComprobante": "codComp: '01' (Factura)",
      "tipoDocReceptor": "codDocRecep: '6' (RUC)"
    }
  },
  "rpta": 1
}`}
                  />
                </div>
              </TabsContent>
            </Tabs>
          </div>

          {/* Códigos de Error */}
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <h3 className="font-bold text-red-900 mb-3">Códigos de Error Comunes</h3>
            <div className="space-y-2 text-sm">
              <div className="flex gap-3">
                <code className="bg-red-100 px-2 py-1 rounded text-red-700 font-mono">401</code>
                <span className="text-red-800">Token inválido o no proporcionado</span>
              </div>
              <div className="flex gap-3">
                <code className="bg-red-100 px-2 py-1 rounded text-red-700 font-mono">404</code>
                <span className="text-red-800">Documento no encontrado</span>
              </div>
              <div className="flex gap-3">
                <code className="bg-red-100 px-2 py-1 rounded text-red-700 font-mono">429</code>
                <span className="text-red-800">Límite de consultas excedido</span>
              </div>
              <div className="flex gap-3">
                <code className="bg-red-100 px-2 py-1 rounded text-red-700 font-mono">500</code>
                <span className="text-red-800">Error interno del servidor</span>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
